import { assetUrl, http, orUndefined, storeImage } from "./http";
import type { EmploymentStatus, Salary, Sexe, Staff, StaffFunction, Teacher } from "@/types";

/* -------------------------------------------------------------------------- */
/* Format du backend                                                           */
/* -------------------------------------------------------------------------- */

interface ApiPerson {
  id: number;
  firstName: string;
  lastName: string;
  phone: string | null;
  email: string | null;
  cycleId: number;
  gender: Sexe | null;
  photoUrl: string | null;
  status: EmploymentStatus;
  hireDate: string | null;
}

interface ApiTeacher extends ApiPerson {
  weeklyHours: number | null;
  hourlyRate: number | null;
  fixedMonthlySalary: number | null;
  primaryClassId: number | null;
  userId: number | null;
  assignments: Array<{ classId: number; subjectIds: number[] }>;
}

interface ApiStaff extends ApiPerson {
  functionId: number | null;
}

interface ApiSalary {
  id: number;
  personId: number;
  personType: "ENSEIGNANT" | "PERSONNEL";
  amount: number;
  cycleId: number;
}

const toPerson = (p: ApiPerson) => ({
  id: String(p.id),
  firstName: p.firstName,
  lastName: p.lastName,
  phone: p.phone ?? "",
  email: p.email ?? "",
  cycleId: String(p.cycleId),
  gender: p.gender ?? ("M" as Sexe),
  photo: assetUrl(p.photoUrl),
  status: p.status,
  hireDate: p.hireDate ?? "",
});

const toTeacher = (t: ApiTeacher): Teacher => ({
  ...toPerson(t),
  weeklyHours: t.weeklyHours ?? undefined,
  hourlyRate: t.hourlyRate ?? undefined,
  fixedMonthlySalary: t.fixedMonthlySalary ?? undefined,
  primaryClassId: t.primaryClassId ? String(t.primaryClassId) : undefined,
  userId: t.userId ? String(t.userId) : undefined,
  assignments: (t.assignments ?? []).map((a) => ({ classId: String(a.classId), subjectIds: a.subjectIds.map(String) })),
});

const toStaff = (s: ApiStaff): Staff => ({ ...toPerson(s), functionId: s.functionId ? String(s.functionId) : "" });

const toSalary = (s: ApiSalary): Salary => ({
  id: String(s.id),
  personId: String(s.personId),
  personType: s.personType === "ENSEIGNANT" ? "enseignant" : "personnel",
  amount: s.amount,
  cycleId: String(s.cycleId),
});

/** Champs communs envoyés au backend (seuls les champs présents dans `p` sont envoyés). */
async function personBody(p: Partial<Teacher | Staff>) {
  return {
    firstName: p.firstName?.trim(),
    lastName: p.lastName?.trim(),
    phone: orUndefined(p.phone),
    email: orUndefined(p.email),
    cycleId: p.cycleId ? Number(p.cycleId) : undefined,
    gender: p.gender,
    photoUrl: "photo" in p ? await storeImage(p.photo) : undefined,
    status: p.status,
    hireDate: orUndefined(p.hireDate),
  };
}

async function teacherBody(t: Partial<Teacher>) {
  return {
    ...(await personBody(t)),
    weeklyHours: t.weeklyHours,
    hourlyRate: t.hourlyRate,
    // Clé présente : on fixe le salaire, ou on revient au tarif horaire avec null.
    fixedMonthlySalary: "fixedMonthlySalary" in t ? (t.fixedMonthlySalary ?? null) : undefined,
    // Clé présente : on fixe (ou retire, avec null) la classe titulaire / on remplace les affectations.
    primaryClassId: "primaryClassId" in t ? (t.primaryClassId ? Number(t.primaryClassId) : null) : undefined,
    userId: "userId" in t ? (t.userId ? Number(t.userId) : null) : undefined,
    assignments:
      "assignments" in t
        ? (t.assignments ?? []).map((a) => ({ classId: Number(a.classId), subjectIds: a.subjectIds.map(Number) }))
        : undefined,
  };
}

/* Enseignants ------------------------------------------------------------- */

export async function getTeachers(): Promise<Teacher[]> {
  return (await http.getAll<ApiTeacher>("/teachers")).map(toTeacher);
}

export async function addTeacher(payload: Omit<Teacher, "id">): Promise<Teacher> {
  // Pas de salaire fixe : la paie d'un enseignant = heures / semaine × tarif horaire.
  return toTeacher(await http.post<ApiTeacher>("/teachers", await teacherBody(payload)));
}

export async function updateTeacher(id: string, patch: Partial<Teacher>): Promise<Teacher> {
  return toTeacher(await http.put<ApiTeacher>(`/teachers/${id}`, await teacherBody(patch)));
}

/** Crée le compte de l'enseignant (rôle de son cycle, mot de passe provisoire) ; `email` complète sa fiche. */
export async function createTeacherAccount(
  id: string,
  email?: string,
): Promise<{ email: string; firstName: string; lastName: string; temporaryPassword: string; emailSent: boolean }> {
  return http.post(`/teachers/${id}/account`, email ? { email: email.trim() } : {});
}

export async function deleteTeacher(id: string): Promise<void> {
  await http.delete(`/teachers/${id}`);
}

/* Personnel --------------------------------------------------------------- */

export async function getStaff(): Promise<Staff[]> {
  return (await http.getAll<ApiStaff>("/staff")).map(toStaff);
}

export async function addStaff(payload: Omit<Staff, "id">): Promise<Staff> {
  // Le salaire se crée au premier montant saisi dans l'onglet « Salaires » (voir saveStaffSalary).
  return toStaff(
    await http.post<ApiStaff>("/staff", { ...(await personBody(payload)), functionId: Number(payload.functionId) }),
  );
}

export async function updateStaff(id: string, patch: Partial<Staff>): Promise<Staff> {
  return toStaff(
    await http.put<ApiStaff>(`/staff/${id}`, {
      ...(await personBody(patch)),
      functionId: patch.functionId ? Number(patch.functionId) : undefined,
    }),
  );
}

export async function deleteStaff(id: string): Promise<void> {
  await http.delete(`/staff/${id}`);
}

/* Fonctions --------------------------------------------------------------- */

export async function getStaffFunctions(): Promise<StaffFunction[]> {
  return (await http.getAll<{ id: number; name: string }>("/staff-functions")).map((f) => ({
    id: String(f.id),
    name: f.name,
  }));
}

export async function addStaffFunction(name: string): Promise<StaffFunction> {
  const created = await http.post<{ id: number; name: string }>("/staff-functions", { name: name.trim() });
  return { id: String(created.id), name: created.name };
}

export async function updateStaffFunction(id: string, name: string): Promise<StaffFunction> {
  const updated = await http.put<{ id: number; name: string }>(`/staff-functions/${id}`, { name: name.trim() });
  return { id: String(updated.id), name: updated.name };
}

export async function deleteStaffFunction(id: string): Promise<void> {
  // Refusé par le backend si la fonction est encore attribuée (message affiché tel quel).
  await http.delete(`/staff-functions/${id}`);
}

/* Salaires fixes (personnel non enseignant uniquement) -------------------- */

export async function getSalaries(): Promise<Salary[]> {
  return (await http.getAll<ApiSalary>("/salaries", { personType: "PERSONNEL" })).map(toSalary);
}

/** Enregistre le salaire fixe d'un membre du personnel : met à jour sa ligne, ou la crée s'il n'en a pas. */
export async function saveStaffSalary(input: {
  salaryId?: string;
  personId: string;
  cycleId: string;
  amount: number;
}): Promise<Salary> {
  if (input.salaryId) return updateSalary(input.salaryId, input.amount);
  return toSalary(
    await http.post<ApiSalary>("/salaries", {
      personId: Number(input.personId),
      personType: "PERSONNEL",
      amount: input.amount,
      cycleId: Number(input.cycleId),
    }),
  );
}

export async function updateSalary(id: string, amount: number): Promise<Salary> {
  return toSalary(await http.put<ApiSalary>(`/salaries/${id}`, { amount }));
}
